import * as dashboardService from "../../../../../../services/Dashboard";
import { OnboardingNewUiUxReduxActions } from "../onboardingNewUiUx.action";
import * as _ from "lodash";
import { MONTHS } from "./constants";

export const EducationReduxActionTypes = {
  RESET_STATE_DATA: "RESET_STATE_DATA",
  UPDATE_FORM_INSTANCE: "UPDATE_FORM_INSTANCE",
};

const updateParent = (values) => (dispatch) => {
  dispatch({
    type: OnboardingNewUiUxReduxActions.UPDATE_SECTION_VALUES,
    payload: {
      page: "education_page",
      values,
    },
  });
};

const setFormInstance = (form) => (dispatch) => {
  dispatch({
    type: EducationReduxActionTypes.UPDATE_FORM_INSTANCE,
    payload: { form },
  });
};

/**
 * Validates the education data on the education page of the onboarding UI.
 * 
 * @param {Function} dispatch - The Redux dispatch function.
 * @param {Function} getState - The Redux function to retrieve the current state.
 * @returns {void}
 * 
 * This function checks each entry in the education data for required fields and ensures that
 * the end date is valid if provided. If any validation errors are found, it dispatches an 
 * action to update the error state for the education page.
 * 
 * Validation includes:
 *  - Required fields: end_year, end_month, start_year, start_month, college, degree, level.
 *  - If months are provided, it checks that the end month is not before the start month.
 *  - If the education is marked as current, end date fields are not validated.
 */
export const VALIDATE_EDUCATION_PAGE_DATA = (dispatch, getState) => {
  const state = getState();
  const { education_page } = state.onboarding_new_ui;
  let newErrorObject = {};

  /**
   * Checks if the end date is valid compared to the start date.
   * 
   * @param {Object} item - The education entry containing start and end date information.
   * @returns {boolean} - True if the end date is valid, false otherwise.
   */
  const isEndDateValid = ({ start_year, start_month, end_year, end_month }) => {
    if (!end_year) return true;
    if (end_year > start_year) return true;
    if (end_year === start_year && start_month && end_month) {
      return MONTHS.indexOf(end_month) >= MONTHS.indexOf(start_month);
    }
    return end_year === start_year;
  };

  /**
   * Checks if a field is required and adds an error if not present.
   * 
   * @param {Object} entry - The education entry.
   * @param {string} key - The key to check.
   * @param {number} index - The index of the entry in the array.
   */
  const validateRequiredField = (entry, key, index) => {
    if (!entry[key] && !["start_month", "end_month"].includes(key)) {
      newErrorObject[`education,${index},${key}`] = ["required"];
    }
  };

  const education_entries = education_page.values;

  if (Array.isArray(education_entries) && !education_entries.length) {
    ['end_year', 'start_year', 'college', 'degree', 'level'].forEach(field => {
      newErrorObject[`education,0,${field}`] = ["required"];
    });
  }

  education_entries.forEach((entry, index) => {
    Object.keys(entry).forEach(key => {
      if (["is_current", "country_id"].includes(key)) return;
      if (["end_year", "end_month"].includes(key) && entry.is_current) return;
      if (["start_month", "end_month"].includes(key) && entry[key] === '') return;

      validateRequiredField(entry, key, index);

      if (key === "end_year" && entry.start_year && !isEndDateValid(entry)) {
        newErrorObject[`education,${index},${key}`] = [`invalid ${key}`];
      }
    });
  });

  dispatch({
    type: OnboardingNewUiUxReduxActions.UPDATE_ERROR_DATA,
    payload: { page: "education_page", errors: newErrorObject },
  });
};



const saveEducation = (values) => async () => {
  await dashboardService.saveEducationInfoV2(values);
};

const resetStateData = () => ({
  type: EducationReduxActionTypes.RESET_STATE_DATA,
});

export const EducationActions = {
  resetStateData,
  updateParent,
  saveEducation,
  setFormInstance,
};
